import { isLayoutLevelId, parseLayoutLevelId } from '../config/levels.js';

export const STANDARD_BREAKOUT_TEMPLATE = {
  id: 'standard-lounge',
  name: 'Standard Lounge',
};

const FETCH_TIMEOUT_MS = 5000;

async function fetchJson(path) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(path, { signal: controller.signal });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Platform-curated templates plus the built-in, asset-light lounge.
 * @returns {Promise<Array<{id: string, name: string, isDefault?: boolean}>>}
 */
export async function fetchBreakoutTemplates() {
  const body = await fetchJson('/api/breakout-templates');
  if (!body || !Array.isArray(body.templates)) {
    throw new Error('Breakout template catalog unavailable');
  }
  const remote = body.templates
    .filter(
      (template) =>
        isLayoutLevelId(template?.id) &&
        typeof template?.name === 'string' &&
        template.name.trim().length > 0,
    )
    .map((template) => ({
      id: template.id,
      name: template.name,
      isDefault: template.isDefault === true,
    }));
  return [STANDARD_BREAKOUT_TEMPLATE, ...remote];
}

/**
 * The template the host panel should pre-select: the platform-flagged default
 * layout when the catalog carries one, else the built-in Standard Lounge.
 * @param {Array<{id: string, isDefault?: boolean}>} templates
 * @returns {string}
 */
export function defaultBreakoutTemplateId(templates) {
  const match = (templates || []).find((template) => template?.isDefault === true);
  return match?.id || STANDARD_BREAKOUT_TEMPLATE.id;
}

/**
 * Fetch a curated editor layout for a breakout resolver.
 * @param {string} templateId
 * @returns {Promise<{id: string, name: string, data: Object}|null>}
 */
export async function fetchBreakoutTemplate(templateId) {
  const layoutId = parseLayoutLevelId(templateId);
  if (!layoutId) return null;
  const body = await fetchJson(`/api/breakout-templates/${encodeURIComponent(layoutId)}`);
  const template = body?.template;
  if (
    !template ||
    template.id !== templateId ||
    typeof template.name !== 'string' ||
    !template.data ||
    !Array.isArray(template.data.walls)
  ) {
    return null;
  }
  return { id: template.id, name: template.name, data: template.data };
}
